import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, RouterOutlet, NavigationStart, NavigationCancel, NavigationEnd, NavigationError } from '@angular/router';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ActivityService, AppSecurityService, AuthService, IRoleView, LayoutService, LookupService, ProfilePhotoService, ThemeService } from './shared/services';
import { IAuthTokenDetail } from './shared/entities';
import { IRoleAdmin, RolesService } from './admin/roles/roles.service';
import { FeatureKey, MenuKey } from './shared/enums';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatSidenav } from '@angular/material/sidenav';
import { routeFadeUp } from './shared/animations/route.animations';
import { AttendanceService } from './shared/services/attendance.service';
declare let $: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [
        Location, {
            provide: LocationStrategy,
            useClass: PathLocationStrategy
        }
    ],
    animations: [routeFadeUp],
    standalone: false
})
export class AppComponent implements OnInit {
    location: any;
    routerSubscription: any;
    public isUserLoggedIn!: Observable<boolean>;
    public userDetails: IAuthTokenDetail | null = null;
    /** Microsoft Graph profile photo (object URL) for the avatar; null => show initials. */
    public avatarUrl: string | null = null;
    @ViewChild(MatSidenav) sidenav!: MatSidenav;
    public isMobile= true;
    /** Desktop sidebar collapsed state - remembered per browser so the choice sticks across visits. */
    public isCollapsed = AppComponent.readSidebarCollapsed();

    /**
     * Reduced-motion gate for the route transition (Conexa Flow): read ONCE at startup and bound
     * to [@.disabled] on the route-outlet wrapper, so those users get no route animation. The
     * CSS-driven motion is handled separately by the global prefers-reduced-motion rules.
     * This is a display-only preference read - no behavior/logic depends on it.
     */
    public readonly reducedMotion: boolean =
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
            : false;

    /** Exposed to the template so the sidebar can gate items per feature grant. */
    public readonly FeatureKey = FeatureKey;
    public readonly attendancePending$ = this.attendanceService.pendingCounts$;

    // Fallback labels for the original built-in roles, used until the live role-type lookup resolves
    // (and if it ever fails). The authoritative labels - including every CUSTOM role - come from
    // roleNameById below, populated from GET /lookup/role-types so any role shows its real name.
    private readonly roleLabels: { [roleId: string]: string } = {
        '1': 'Admin',
        '2': 'Accounting',
        '3': 'Manager',
        '4': 'Technician',
        '5': 'Subcontractor',
        '6': 'Accounting Supervisor',
        '7': 'Executive Admin',
        '8': 'New Employee'
    };

    // roleId -> role name for ALL roles (built-in + custom), loaded from the role-type lookup so the
    // top-bar chip shows the correct name for every role, not just the hardcoded built-ins.
    private roleNameById: { [roleId: string]: string } = {};

    private static readonly chunkReloadStampKey = 'conexa-chunk-reload-at';

    /**
     * Does this navigation error mean a lazy chunk failed to LOAD (vs. a real routing error)? Covers
     * webpack's ChunkLoadError plus the native dynamic-import wording the esbuild builder produces
     * ("Failed to fetch dynamically imported module" / "error loading dynamically imported module").
     */
    public static isStaleChunkError(error: unknown): boolean {
        const text = `${(error as { name?: string })?.name ?? ''} ${(error as { message?: string })?.message ?? error ?? ''}`;
        return /ChunkLoadError|Loading chunk|dynamically imported module/i.test(text);
    }

    // --- "View as [Role]" (Admins only) ---
    // The roles an Admin can preview: everything except Admin itself (that IS the current view) and
    // Technician/Subcontractor (they can never access the admin portal, so there is nothing to see).
    public viewableRoles: IRoleAdmin[] = [];
    private roleViewListLoaded = false;
    private static readonly nonViewableRoleIds: number[] = [1, 4, 5];

    /** Initials for the top-bar profile avatar (e.g. "Casey Cristobal" -> "CC"). */
    public get initials(): string {
        const name = this.userDetails?.name?.trim();
        if (!name) return '';
        const parts = name.split(/\s+/);
        const first = parts[0]?.charAt(0) ?? '';
        const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
        return (first + last).toUpperCase();
    }

    /** Friendly role label for the top-bar profile chip (works for built-in AND custom roles). */
    public get roleLabel(): string {
        const id = this.userDetails?.roleId;
        if (!id) return '';
        // Prefer the name carried on the profile (SSO /user/me), then the live role-type lookup (covers
        // custom roles in JWT mode too), then the built-in fallback labels.
        return this.userDetails?.roleName || this.roleNameById[id] || this.roleLabels[id] || '';
    }

    constructor(private router: Router,
        private authService: AuthService,
        private appSecurityService: AppSecurityService,
        private themeService: ThemeService,
        private layoutService: LayoutService,
        private profilePhotoService: ProfilePhotoService,
        private lookupService: LookupService,
        private activityService: ActivityService,
        private rolesService: RolesService,
        private observer: BreakpointObserver,
        private attendanceService: AttendanceService) {
            this.isUserLoggedIn = this.authService.isUserLoggedIn;
            // Apply the user's saved theme + layout as early as possible.
            this.themeService.init();
            this.layoutService.init();
    }

    public ngOnInit(): void {
        this.recallJsFuntions();
        this.getTokenDetails();
        this.startActivityHeartbeat();

        // Compact-shell breakpoint: phones AND portrait tablets get the overlay drawer + hamburger
        // (a persistent 232px sidebar leaves too little room for the data tables below ~1024px).
        // Keep in sync with the .header-nav media query in app.component.scss.
        this.observer.observe(['(max-width: 1024px)']).subscribe((screenSize) => {
            if(screenSize.matches){
              this.isMobile = true;
            } else {
              this.isMobile = false;
            }
          });
    }

    public logOut(): void {
        this.authService.logout();
        this.router.navigate(['/login']);
    }

    /**
     * Pings the activity heartbeat now and every 5 minutes while signed in, so the server can record
     * login sessions + who's currently active (Logins & User History). No-op / harmless when logged out.
     */
    private startActivityHeartbeat(): void {
        const ping = (): void => {
            if (this.authService.isLoggedIn()) {
                this.activityService.heartbeat().subscribe({ error: () => { /* best-effort */ } });
            }
        };
        ping();
        setInterval(ping, 5 * 60 * 1000);
    }

    public toggleMenu(): void {
        if(this.isMobile){
            this.sidenav.toggle();
            this.isCollapsed = false; // The compact shell is an overlay drawer - open/close, never collapsed
          } else {
            // Desktop/tablet: collapse or expand the persistent sidebar. Material animates the
            // drawer slide + the content margin, so both move together smoothly.
            this.isCollapsed = !this.isCollapsed;
            try {
                localStorage.setItem('sidebar-collapsed', this.isCollapsed ? '1' : '0');
            } catch { /* storage unavailable (private mode) - the toggle still works for the session */ }
            if (this.isCollapsed) {
                this.sidenav.close();
            } else {
                this.sidenav.open();
            }
          }
      }

    private static readSidebarCollapsed(): boolean {
        try {
            return localStorage.getItem('sidebar-collapsed') === '1';
        } catch {
            return false;
        }
    }

    public getTokenDetails(): void {
        this.userDetails = this.authService.getTokenDetails();
        if (this.userDetails) {
            // Best-effort: pull the Microsoft profile photo; silently keeps initials on any failure.
            this.profilePhotoService.getPhotoUrl().then(url => this.avatarUrl = url);
            // Resolve role names for ALL roles (built-in + custom) so the chip shows the real name.
            // Cached + best-effort: on any failure the chip falls back to the built-in labels.
            this.lookupService.getRoleTypes().subscribe({
                next: roles => roles.forEach(r => this.roleNameById[String(r.id)] = r.description),
                error: () => { /* keep built-in fallback labels */ }
            });
        }
    }

    // --- "View as [Role]" (Admins only): preview the portal exactly as another role sees it. ---

    /** Gate on the REAL signed-in role (never the preview), so the control survives any preview. */
    public isRealAdmin(): boolean {
        return this.authService.isUserAdmin();
    }

    /** The role currently being previewed (null = the admin's own view). */
    public get roleView(): IRoleView | null {
        return this.authService.activeRoleView;
    }

    /** Lazily load the previewable roles the first time the admin opens the View-as menu. */
    public loadViewableRoles(): void {
        if (this.roleViewListLoaded || !this.isRealAdmin()) return;
        this.rolesService.getRoles().subscribe({
            next: roles => {
                this.viewableRoles = (roles ?? []).filter(r => !AppComponent.nonViewableRoleIds.includes(r.id));
                this.roleViewListLoaded = true;
            },
            error: () => { /* best-effort - the menu just shows the loading hint until it works */ }
        });
    }

    /** Start previewing as the picked role and land on the dashboard, like that role would. */
    public startRoleView(role: IRoleAdmin): void {
        const started = this.authService.startRoleView({
            roleId: role.id,
            roleName: role.description,
            features: role.features ?? [],
            menus: role.menus ?? []
        });
        if (started) {
            this.router.navigate(['/home']);
        }
    }

    /** Exit the preview and return to the admin's own view. */
    public endRoleView(): void {
        this.authService.endRoleView();
        this.router.navigate(['/home']);
    }

    public hasAdminAreaAccess(): boolean {
        return this.appSecurityService.hasAccessToAdminArea();
    }

    public hasLeadsAreaAccess(): boolean {
        return this.appSecurityService.hasAccessToLeadsArea();
    }

    public hasSurveysAreaAccess(): boolean {
        return this.appSecurityService.hasAccessToSurveyArea();
    }

    public hasSupervisorAreaAccess(): boolean {
        return this.appSecurityService.hasAccessToSupervisorArea();
    }

    public hasAttendanceAreaAccess(): boolean {
        return this.appSecurityService.hasAccessToAttendanceArea()
            || this.appSecurityService.hasFeature(FeatureKey.ClockEditAny);
    }

    // PTO Requests: employees request their own time off (pto-requests) OR managers work the
    // approval queue (pto-approve-manager) - either grant shows the sidebar item.
    public hasPtoAreaAccess(): boolean {
        return this.appSecurityService.hasFeature(FeatureKey.PtoRequests)
            || this.appSecurityService.hasFeature(FeatureKey.PtoManagerApprove);
    }

    public hasExecutiveAreaAccess(): boolean {
        return this.appSecurityService.hasAccessToExecutiveArea();
    }

    /** Generic per-feature check, used by the grouped sidebar items. */
    public has(feature: FeatureKey): boolean {
        return this.appSecurityService.hasFeature(feature);
    }

    /**
     * Keys the route fade-up transition on the activated top-level route (the standard
     * outlet-data animation pattern), so workspace-to-workspace navigation animates while
     * navigation inside a lazy module (same top-level component) stays still. Guarded on
     * isActivated - reading activatedRoute on a deactivated outlet throws.
     */
    public prepareRoute(outlet: RouterOutlet | null): string {
        return outlet && outlet.isActivated
            ? (outlet.activatedRoute.snapshot.routeConfig?.path ?? '')
            : '';
    }

    // --- Module visibility: a menu shows iff the role is granted that menu (explicit grant). The
    //     feature grants within then control which tiles/items appear on the menu's hub page. ---

    public showOperationsModule(): boolean {
        return this.appSecurityService.hasMenu(MenuKey.Operations);
    }

    public showBillingModule(): boolean {
        return this.appSecurityService.hasMenu(MenuKey.Billing);
    }

    public showSalesModule(): boolean {
        return this.appSecurityService.hasMenu(MenuKey.Sales);
    }

    public showCSuiteModule(): boolean {
        return this.appSecurityService.hasMenu(MenuKey.CSuite);
    }

    public showHrModule(): boolean {
        return this.appSecurityService.hasMenu(MenuKey.Hr);
    }

    public showIstModule(): boolean {
        return this.appSecurityService.hasMenu(MenuKey.Ist);
    }

    public showAdministratorModule(): boolean {
        return this.appSecurityService.hasMenu(MenuKey.Administrator);
    }

    private recallJsFuntions(): void {
        this.router.events
        .subscribe((event) => {
            if ( event instanceof NavigationStart ) {
                $('.loader-content').fadeIn('slow');
            }
            // A lazy chunk failed to load mid-navigation. The bundles are content-hashed, so this
            // almost always means a deploy replaced them while this tab was still running the previous
            // build - the old hashed chunk 404s and the user is stranded (Kathy hit this on prod right
            // after a web deploy). A full page load picks up the fresh index + chunks and lands on the
            // page they were navigating to. Guarded to once per 30s so a genuine outage (chunks still
            // failing after the reload) can never reload-loop.
            if (event instanceof NavigationError && AppComponent.isStaleChunkError(event.error)) {
                let lastReload = 0;
                try { lastReload = Number(sessionStorage.getItem(AppComponent.chunkReloadStampKey) ?? 0); } catch { /* storage blocked */ }
                if (Date.now() - lastReload > 30000) {
                    try { sessionStorage.setItem(AppComponent.chunkReloadStampKey, String(Date.now())); } catch { /* storage blocked */ }
                    window.location.assign(event.url);
                }
            }
        });
        this.routerSubscription = this.router.events
        .pipe(filter(event => event instanceof NavigationEnd || event instanceof NavigationCancel))
        .subscribe(event => {
            $.getScript('../assets/js/custom.js');
            $('.loader-content').fadeOut('slow');
            this.location = this.router.url;
            if (!(event instanceof NavigationEnd)) {
                return;
            }
            // On the compact shell the sidenav is an overlay - close it after tapping a nav link so
            // the destination page isn't left hidden behind the open drawer.
            if (this.isMobile && this.sidenav?.opened) {
                this.sidenav.close();
            }
            window.scrollTo(0, 0);
        });
    }
}
